import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { MiniBarChart } from "../../../shared/ui";
import { getAdminStats, getAdminTimeseries, type AdminStats, type AdminTimeseries } from "../../../features/admin/api/adminApi";
import { PageHeader, StatCard, money } from "./_shared";

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [ts, setTs] = useState<AdminTimeseries | null>(null);

  useEffect(() => {
    getAdminStats().then(setStats).catch(() => undefined);
    getAdminTimeseries(30).then(setTs).catch(() => undefined);
  }, []);

  // Bucket the daily series into ~14 columns for a compact chart.
  const bucket = (key: "signups" | "revenue") => {
    if (!ts) return [];
    const series = ts.series;
    const groups = 14;
    const size = Math.ceil(series.length / groups);
    const out: { label: string; value: number }[] = [];
    for (let i = 0; i < series.length; i += size) {
      const slice = series.slice(i, i + size);
      const value = slice.reduce((s, d) => s + d[key], 0);
      const label = new Date(slice[0].date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
      out.push({ label, value });
    }
    return out;
  };

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Platform overview" />
      <div className="p-4 sm:p-6">
        {!stats ? (
          <div className="flex h-40 items-center justify-center text-ink-400">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <StatCard label="Users" value={stats.users.total} hint={`${stats.users.approved} approved`} />
              <StatCard label="Pending" value={stats.users.pending} tone={stats.users.pending ? "gold" : "ink"} hint="awaiting approval" />
              <StatCard label="Workspaces" value={stats.workspaces} />
              <StatCard label="Models" value={stats.models} hint={`${stats.embeds.views.toLocaleString()} embed views`} />
              <StatCard label="Active subs" value={stats.activeSubscriptions} tone="emerald" />
              <StatCard label="MRR" value={money(stats.mrr)} tone="gold" hint={`${money(stats.mrr * 12)} ARR`} />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="surface-card p-5">
                <h3 className="text-[13px] font-semibold text-ink-900">New signups</h3>
                <p className="text-[11.5px] text-ink-400">Last 30 days</p>
                <div className="mt-4">{ts ? <MiniBarChart data={bucket("signups")} tone="ink" height={120} /> : null}</div>
              </div>
              <div className="surface-card p-5">
                <h3 className="text-[13px] font-semibold text-ink-900">Revenue (paid invoices)</h3>
                <p className="text-[11.5px] text-ink-400">Last 30 days</p>
                <div className="mt-4">{ts ? <MiniBarChart data={bucket("revenue")} tone="gold" height={120} /> : null}</div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="surface-card p-5">
                <h3 className="text-[13px] font-semibold text-ink-900">Plan distribution</h3>
                <div className="mt-3 space-y-2">
                  {stats.planDistribution.map((p) => {
                    const max = Math.max(...stats.planDistribution.map((x) => x.count), 1);
                    return (
                      <div key={p.name} className="flex items-center gap-3">
                        <span className="w-28 shrink-0 truncate text-[12px] text-ink-600">{p.name}</span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                          <div className="h-full rounded-full bg-gold-400" style={{ width: `${(p.count / max) * 100}%` }} />
                        </div>
                        <span className="w-8 text-right text-[12px] tabular text-ink-700">{p.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {stats.users.pending > 0 && (
                <Link to="/admin/users?accountStatus=pending" className="surface-card flex flex-col justify-center p-5 transition-colors hover:border-gold-300">
                  <p className="text-[13px] font-semibold text-ink-900">{stats.users.pending} user{stats.users.pending > 1 ? "s" : ""} awaiting approval</p>
                  <p className="mt-1 text-[12px] text-ink-500">Review and approve pending accounts →</p>
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
